import React, { useEffect, useState } from 'react';
import ReactSelect from '@components/atoms/ReactSelect';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { Crew } from '@models/crew.model';
import { Organization } from '@models/organization.model';
import { UserRoles } from '@models/user.model';
import { Task } from '@models/task.model';
import { SingleValue, MultiValue } from 'react-select';
import { SelectOption } from '@components/atoms/ReactSelect';
import { useCrews } from '@hooks/api/useCrews';
import { useOrganizations } from '@hooks/api/useOrganizations';
import { useSuperintendents } from '@hooks/api/useSuperintendents';
import { useTasks } from '@hooks/api/task/useTasks';
import { useAppSelector } from '@redux/hooks';
import { useBudget } from '@hooks/api/useBudget';
import { BudgetRequest, BudgetRequestStatus } from '@models/budget.model';
import BudgetRequestModal from '@components/organisms/BudgetRequestModal';
import { CancelWarningModal } from './CancelWarningModal';
import { SelectedCrewMembers } from './SelectedCrewMembers';
import Alert from '@components/atoms/Alert';
import { Button } from '@components/atoms/Button';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useFeatureAccess } from '@utils/featureFlag';

interface AssignmentTabProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (
    field: string,
    touched: boolean,
    shouldValidate?: boolean,
  ) => void;
  validateForm: () => Promise<any>;
  handleBlur: (e: React.FocusEvent<any>) => void;
  errors: any;
  touched: any;
  projectId: string;
  taskId?: string;
  delegationType: 'crew' | 'subcontractor';
  setDelegationType: (type: 'crew' | 'subcontractor') => void;
  selectedCrews: Crew[];
  setSelectedCrews: (crews: Crew[]) => void;
  selectedOrganization: Organization | null;
  setSelectedOrganization: (org: Organization | null) => void;
  initialData?: any;
  isDelegated?: boolean;
  subtasks?: any[];
  onTaskDataChange?: () => void; // Callback to refresh parent task data
  budgetRequestError?: string | null;
  onBudgetRequestErrorDismiss?: () => void;
}

export const AssignmentTab: React.FC<AssignmentTabProps> = ({
  values,
  errors,
  touched,
  subtasks,
  projectId,
  taskId,
  initialData,
  selectedCrews,
  setFieldValue,
  setFieldTouched,
  validateForm,
  handleBlur,
  delegationType,
  setDelegationType,
  setSelectedCrews,
  setSelectedOrganization,
  isDelegated,
  onTaskDataChange,
  budgetRequestError,
  onBudgetRequestErrorDismiss,
}) => {
  const { organizationId } = useAppSelector((state) => state.organization);
  const { role } = useAppSelector((state) => state.user);
  const { canAccess } = useFeatureAccess();
  const canShowBudgetTab = canAccess('BUDGET_TAB');

  // Custom hooks
  const { crews, loading: crewsLoading, fetchCrews } = useCrews();
  const {
    organizations,
    loading: orgsLoading,
    fetchOrganizations,
  } = useOrganizations();
  const { superintendents, loading: supLoading } =
    useSuperintendents(projectId);
  const {
    fetchBudgetRequestsByTaskId,
    createBudgetRequest,
    cancelBudgetRequest,
    loading: budgetActionLoading,
  } = useBudget();
  const { cancelDelegation, updateTask } = useTasks();

  // Budget state management
  const [existingBudgetRequests, setExistingBudgetRequests] = useState<
    BudgetRequest[]
  >([]);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [showBudgetModal, setShowBudgetModal] = useState(false);

  // Cancel warning modal state
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [cancelType, setCancelType] = useState<'budget' | 'delegation' | null>(
    null,
  );

  // Fetch data when delegation type changes
  useEffect(() => {
    if (organizationId) {
      fetchCrews(organizationId);
    }
    fetchOrganizations();
  }, [organizationId, fetchCrews, fetchOrganizations]);

  // Fetch existing budget requests when taskId changes
  useEffect(() => {
    if (taskId && canShowBudgetTab) {
      setBudgetLoading(true);
      fetchBudgetRequestsByTaskId(taskId)
        .then((budgetRequests) => {
          setExistingBudgetRequests(budgetRequests || []);
        })
        .finally(() => {
          setBudgetLoading(false);
        });
    } else {
      setExistingBudgetRequests([]);
    }
  }, [taskId, fetchBudgetRequestsByTaskId, canShowBudgetTab]);

  // Initialize crews and organizations from initial data only once
  useEffect(() => {
    // Only run this effect once when initialData changes
    if (initialData) {
      // Handle crews initialization
      if (initialData.crews && initialData.crews.length > 0) {
        setSelectedCrews(initialData.crews);
        setDelegationType('crew');
        setFieldValue('delegationType', 'crew');
      }

      // Handle organization initialization
      if (initialData.delegateOrganization) {
        setSelectedOrganization(initialData.delegateOrganization);
        setDelegationType('subcontractor');
        setFieldValue('delegationType', 'subcontractor');
      }
    }
  }, [
    initialData,
    setSelectedCrews,
    setDelegationType,
    setFieldValue,
    setSelectedOrganization,
  ]);

  // Handle form value changes separately
  useEffect(() => {
    if (!initialData) {
      // Only handle form value changes when not editing an existing task
      if (values.crewIds && values.crewIds.length > 0) {
        setSelectedCrews(
          crews.filter((crew) => values.crewIds.includes(crew.crewId!)),
        );
      }
      if (values.delegateOrganizationId) {
        setSelectedOrganization(
          organizations.find(
            (org) => org.organizationId === values.delegateOrganizationId,
          ) || null,
        );
      }
    }
  }, [
    values.crewIds,
    values.delegateOrganizationId,
    crews,
    organizations,
    initialData,
    setSelectedCrews,
    setSelectedOrganization,
  ]);

  const handleDelegationTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newType = event.target.checked ? 'subcontractor' : 'crew';

    // Reset selections when switching types
    if (newType === 'subcontractor') {
      setSelectedCrews([]);
      setFieldValue('crewIds', []);
    } else {
      setSelectedOrganization(null);
      setFieldValue('delegateOrganizationId', null);
    }

    setDelegationType(newType);
    setFieldValue('delegationType', newType);
  };

  // Track if this is the initial mount to avoid triggering validation on existing tasks
  const isInitialMount = React.useRef(true);
  const prevDelegationType = React.useRef(delegationType);

  // When delegation type changes to subcontractor, validate and mark error fields as touched
  useEffect(() => {
    // Skip on initial mount or if task already exists (editing mode)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevDelegationType.current = delegationType;
      return;
    }

    // Only trigger validation if delegation type actually changed to subcontractor
    if (
      delegationType === 'subcontractor' &&
      prevDelegationType.current !== 'subcontractor'
    ) {
      // Use a small delay to ensure the delegation type is set in formik first
      const timer = setTimeout(async () => {
        const errors = await validateForm();
        if (Object.keys(errors).length > 0) {
          Object.keys(errors).forEach((field) => {
            setFieldTouched(field, true, false);
          });
        }
      }, 100);

      prevDelegationType.current = delegationType;
      return () => clearTimeout(timer);
    }

    prevDelegationType.current = delegationType;
  }, [delegationType, validateForm, setFieldTouched]);

  const handleBudgetRequestSubmit = async (budgetRequestInput: any) => {
    try {
      const result = await createBudgetRequest(budgetRequestInput);

      if (result) {
        // Check both local state and initial data to determine if task has crew assignments
        const actualTaskCrews = initialData?.crews || [];
        const hasCrewAssignments =
          selectedCrews.length > 0 || actualTaskCrews.length > 0;

        // If task currently has crew assignments (either in local state or actual task data), unassign them
        if (taskId && hasCrewAssignments) {
          try {
            // Update task with empty crew IDs to clear assignments
            const updatePayload = {
              taskId,
              crewIds: [], // Clear crew assignments
            };

            const updateResult = await updateTask(updatePayload);

            // Update local state to reflect the change
            setSelectedCrews([]);
            setFieldValue('crewIds', []);

            // Verify the update worked by checking if the returned task has empty crews
            if (
              updateResult &&
              updateResult.crews &&
              updateResult.crews.length > 0
            ) {
              // Try alternative method: Update with explicit crews field
              try {
                const alternativePayload = {
                  taskId,
                  crews: [], // Alternative field name
                };
                await updateTask(alternativePayload);
              } catch (alternativeError) {
                // Alternative approach failed, but main update may have still worked
              }
            }
          } catch (updateError: any) {
            // Note: We don't throw here because the budget request was successfully created
            // The crew unassignment is a secondary operation
          }
        }

        // Refresh budget requests for this task
        if (taskId) {
          const updatedRequests = await fetchBudgetRequestsByTaskId(taskId);
          setExistingBudgetRequests(updatedRequests || []);
        }

        // Notify parent component to refresh task data
        if (onTaskDataChange) {
          onTaskDataChange();
        }

        setShowBudgetModal(false);
      }
    } catch (error) {
      console.error('Error creating budget request:', error);
    }
  };

  const handleBudgetModalClose = () => {
    setShowBudgetModal(false);
  };

  // Cancel warning modal handlers
  const handleCancelWarningClose = () => {
    setShowCancelWarning(false);
    setCancelType(null);
  };

  const handleCancelWarningConfirm = async () => {
    try {
      if (cancelType === 'budget') {
        const actionableRequest = getActionableBudgetRequest();
        if (actionableRequest?.budgetRequestId) {
          await cancelBudgetRequest(actionableRequest.budgetRequestId);
          // Refresh budget requests to reflect the cancellation
          if (taskId) {
            const updatedRequests = await fetchBudgetRequestsByTaskId(taskId);
            setExistingBudgetRequests(updatedRequests || []);
          }
        }
      } else if (cancelType === 'delegation') {
        if (taskId) {
          await cancelDelegation(taskId);
        }
      }

      // After successful cancellation, allow reassignment
      setFieldValue('reassign', true);

      // Notify parent component to refresh task data
      if (onTaskDataChange) {
        onTaskDataChange();
      }

      setShowCancelWarning(false);
      setCancelType(null);
    } catch (error) {
      console.error('Error canceling:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleCancelBudgetClick = () => {
    setCancelType('budget');
    setShowCancelWarning(true);
  };

  const handleCancelDelegationClick = () => {
    setCancelType('delegation');
    setShowCancelWarning(true);
  };

  const superintendentOptions = superintendents
    .filter((sup) => sup.employeeId)
    .map((sup) => ({
      value: sup.employeeId!,
      label: `${sup.user?.firstName} ${sup.user?.lastName}`,
    }));

  const isDelegationDisabled =
    initialData?.status === 'completed' ||
    (initialData?.delegateOrganizationId && !values.reassign) ||
    (role !== UserRoles.PROJECT_MANAGER &&
      role !== UserRoles.ADMIN &&
      role !== UserRoles.OWNER);

  // Check if task has subtasks - when it does, budget request mode should be disabled
  const hasSubtasks = subtasks && subtasks?.length > 0;
  const isBudgetRequestDisabled = hasSubtasks || !canShowBudgetTab;

  // Force crew mode when task has subtasks
  React.useEffect(() => {
    if (hasSubtasks && delegationType === 'subcontractor') {
      setDelegationType('crew');
      setFieldValue('delegationType', 'crew');
      // Clear organization selection since we're forcing crew mode
      setSelectedOrganization(null);
      setFieldValue('delegateOrganizationId', null);
    }
  }, [
    hasSubtasks,
    delegationType,
    setDelegationType,
    setFieldValue,
    setSelectedOrganization,
  ]);

  // Check if task is currently delegated
  const isCurrentlyDelegated =
    initialData?.delegateTaskId || initialData?.delegateOrganizationId;
  const delegatedToName =
    initialData?.delegateOrganization?.name || 'Unknown Organization';

  // Helper functions for budget status
  const getActionableBudgetRequest = () => {
    return existingBudgetRequests.find(
      (request) => request.status === BudgetRequestStatus.ACTIVE,
    );
  };

  const hasActionableBudgetRequest = !!getActionableBudgetRequest();

  const getBudgetStatusMessage = () => {
    const actionableRequest = getActionableBudgetRequest();
    if (!actionableRequest) return '';

    if (actionableRequest.status === BudgetRequestStatus.ACTIVE) {
      return 'Waiting on Bid Selection / Resolution';
    }

    return 'Bid Request in Progress';
  };

  return (
    <>
      {/* Budget Request Banner with Actions */}
      {canShowBudgetTab && hasActionableBudgetRequest && !values.reassign && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 relative z-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Bid Request Active
                </h3>
                <div className="mt-1 text-sm text-yellow-700">
                  <p>{getBudgetStatusMessage()}</p>
                  {getActionableBudgetRequest() && (
                    <button
                      type="button"
                      className="mt-1 text-xs text-yellow-600 underline hover:text-yellow-800"
                      onClick={() => {
                        const budgetRequest = getActionableBudgetRequest();
                        if (budgetRequest?.budgetRequestId) {
                          window.open(
                            `/budgets?budgetRequestId=${budgetRequest.budgetRequestId}`,
                            '_blank',
                          );
                        }
                      }}>
                      View Bid Request Details
                    </button>
                  )}
                </div>
              </div>
            </div>
            {initialData?.status !== 'completed' && (
              <button
                type="button"
                disabled={
                  role !== UserRoles.PROJECT_MANAGER &&
                  role !== UserRoles.ADMIN &&
                  role !== UserRoles.OWNER
                }
                onClick={handleCancelBudgetClick}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                Cancel Bid Request
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cancel Delegation Button */}
      {isCurrentlyDelegated &&
        (!canShowBudgetTab || !hasActionableBudgetRequest) &&
        initialData?.status !== 'completed' &&
        !values.reassign && (
          <Alert variant="info">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Task Delegated
                  </h3>
                  <p className="mt-1 text-sm text-blue-700">
                    Currently delegated to: <strong>{delegatedToName}</strong>
                  </p>
                </div>
              </div>
              <Button
                type="button"
                disabled={
                  role !== UserRoles.PROJECT_MANAGER &&
                  role !== UserRoles.ADMIN &&
                  role !== UserRoles.OWNER
                }
                onClick={handleCancelDelegationClick}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                Cancel Delegation
              </Button>
            </div>
          </Alert>
        )}

      {/* Assignment Configuration Section */}
      {/* Show assignment section when:
          1. Task is not delegated AND no active budget request, OR
          2. User is actively reassigning (regardless of budget status) */}
      {((!isCurrentlyDelegated &&
        (!canShowBudgetTab || !hasActionableBudgetRequest)) ||
        values.reassign) && (
          <div className="space-y-6">
            {/* Reassignment Notice */}
            {values.reassign && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-400"
                      viewBox="0 0 20 20"
                      fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Ready to Reassign
                    </h3>
                    <p className="mt-1 text-sm text-green-700">
                      {hasActionableBudgetRequest
                        ? 'Previous delegation cancelled. You can now assign to crew or create a new bid request.'
                        : 'Task is ready for reassignment. Choose your assignment type below.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Delegation Type Switch */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    Assignment Type
                  </h3>
                  <p className="text-xs text-gray-600">
                    Assign to your crew or create a bid request for
                    subcontractors
                  </p>
                </div>
                <ToggleSwitch
                  leftLabel="Crew"
                  rightLabel="Create Bid Request"
                  checked={!hasSubtasks && delegationType === 'subcontractor'}
                  onChange={(checked) => {
                    if (!hasSubtasks) {
                      const syntheticEvent = {
                        target: { checked },
                      } as React.ChangeEvent<HTMLInputElement>;
                      handleDelegationTypeChange(syntheticEvent);
                    }
                  }}
                  disabled={isDelegationDisabled || isBudgetRequestDisabled}
                />
              </div>
              {(hasSubtasks || !canShowBudgetTab) && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-4 w-4 text-blue-400"
                        viewBox="0 0 20 20"
                        fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <p className="text-xs text-blue-800">
                        {hasSubtasks
                          ? 'Assignment locked to crew mode because this task has subtasks.'
                          : 'Assignment locked to crew mode. Bid requests are not available for your role.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Crew Assignment */}
            {delegationType === 'crew' && (
              <>
                <FormGroup
                  label="Assign to Crews"
                  htmlFor="crewIds"
                  error={errors.crewIds ? errors.crewIds : undefined}>
                  <ReactSelect
                    options={crews
                      .filter((crew) => crew.crewId)
                      .map((crew) => ({
                        value: crew.crewId!,
                        label: crew.name,
                      }))}
                    value={selectedCrews
                      .filter((crew) => crew.crewId)
                      .map((crew) => ({
                        value: crew.crewId!,
                        label: crew.name,
                      }))}
                    onChange={(
                      option:
                        | SingleValue<SelectOption>
                        | MultiValue<SelectOption>,
                    ) => {
                      if (Array.isArray(option)) {
                        const selectedCrewObjects = option
                          .map((opt) =>
                            crews.find((crew) => crew.crewId === opt.value),
                          )
                          .filter((crew): crew is Crew => crew !== undefined);
                        setSelectedCrews(selectedCrewObjects);
                        setFieldValue(
                          'crewIds',
                          option.map((opt) => opt.value),
                        );

                        // Clear subcontractor when crews are selected
                        if (option.length > 0) {
                          setSelectedOrganization(null);
                          setFieldValue('delegateOrganizationId', null);
                          setDelegationType('crew');
                          setFieldValue('delegationType', 'crew');
                        }
                      } else {
                        setSelectedCrews([]);
                        setFieldValue('crewIds', []);
                      }
                    }}
                    placeholder="Select crews to assign this task"
                    error={!!errors.crewIds}
                    helperText={errors.crewIds}
                    isMulti
                    isDisabled={
                      initialData?.status === 'completed' ||
                      (initialData?.delegateOrganizationId && !values.reassign)
                    }
                  />
                </FormGroup>
                <SelectedCrewMembers crews={selectedCrews} />
              </>
            )}

            {/* Budget Request Error Alert */}
            {budgetRequestError && (
              <Alert variant="error">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ReportProblemIcon className="text-red-600 mr-2" />
                    <p className="text-sm text-red-800">{budgetRequestError}</p>
                  </div>
                  {onBudgetRequestErrorDismiss && (
                    <button type="button"
                      onClick={onBudgetRequestErrorDismiss}
                      className="text-red-600 hover:text-red-800"
                      aria-label="Dismiss error">
                      ×
                    </button>
                  )}
                </div>
              </Alert>
            )}

            {/* Budget Request Information */}
            {canShowBudgetTab && delegationType === 'subcontractor' && !hasSubtasks && (
              <Alert variant="info">
                <div className="flex items-center">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Bid Request Mode
                    </h3>
                    <div className="mt-1 text-sm text-blue-700">
                      <p>
                        You have selected to create a bid request. This will
                        open a modal where you can specify bid details, dates,
                        description, and select recipient organizations.
                      </p>
                      <Button
                        type="button"
                        className="mt-2"
                        onClick={() => setShowBudgetModal(true)}
                        disabled={
                          !taskId ||
                          orgsLoading ||
                          organizations.filter((org) => org.showAsSubcontractor)
                            .length === 0
                        }>
                        {orgsLoading
                          ? 'Loading Organizations...'
                          : organizations.filter((org) => org.showAsSubcontractor)
                            .length === 0
                            ? 'No Subcontractors Available'
                            : 'Open Bid Request Form'}
                      </Button>
                      {!taskId && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          <ReportProblemIcon fontSize="small" />
                          <p>
                            You must save the task first before creating bid
                            requests for subcontractors.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Alert>
            )}
          </div>
        )}

      {/* Task Details Section */}
      <div className="mt-6 space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Details
        </h3>

        {/* Man Hours */}
        <FormGroup
          label="Man Hours (estimated)"
          htmlFor="manHours"
          error={
            errors.manHours && touched.manHours ? errors.manHours : undefined
          }>
          <Input
            type="number"
            id="manHours"
            name="manHours"
            placeholder="120"
            value={values.manHours || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;
              setFieldValue('manHours', value ? Number(value) : null);
            }}
            onBlur={handleBlur}
            className={
              errors.manHours && touched.manHours ? 'border-red-500' : ''
            }
          />
        </FormGroup>

        {/* Time Constraint */}
        <div className="grid grid-cols-2 gap-4">
          <FormGroup
            label="Time Constraint"
            htmlFor="timeConstraint"
            error={
              errors.timeConstraint && touched.timeConstraint
                ? errors.timeConstraint
                : undefined
            }>
            <Input
              type="number"
              id="timeConstraint"
              name="timeConstraint"
              placeholder="120"
              value={values.timeConstraint || ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                setFieldValue('timeConstraint', value ? Number(value) : null);
              }}
              onBlur={handleBlur}
              className={
                errors.timeConstraint && touched.timeConstraint
                  ? 'border-red-500'
                  : ''
              }
            />
          </FormGroup>

          <FormGroup
            label="Time Type"
            htmlFor="typeOfTimeConstraint"
            error={
              errors.typeOfTimeConstraint && touched.typeOfTimeConstraint
                ? errors.typeOfTimeConstraint
                : undefined
            }>
            <ReactSelect
              options={[
                { value: 'hours', label: 'Hours' },
                { value: 'days', label: 'Days' },
                { value: 'weeks', label: 'Weeks' },
              ]}
              value={
                values.typeOfTimeConstraint
                  ? {
                    value: values.typeOfTimeConstraint,
                    label: values.typeOfTimeConstraint,
                  }
                  : null
              }
              onChange={(option) => {
                if (option && !Array.isArray(option) && 'value' in option) {
                  setFieldValue('typeOfTimeConstraint', option.value);
                } else {
                  // Handle clearing the selection
                  setFieldValue('typeOfTimeConstraint', null);
                }
              }}
              onBlur={() => setFieldTouched('typeOfTimeConstraint', true)}
              placeholder="Select time type"
              error={
                !!errors.typeOfTimeConstraint && touched.typeOfTimeConstraint
              }
              helperText={errors.typeOfTimeConstraint}
              isClearable
            />
          </FormGroup>
        </div>

        {/* Superintendent */}
        {(role === UserRoles.OWNER ||
          role === UserRoles.ADMIN ||
          role === UserRoles.PROJECT_MANAGER ||
          role === UserRoles.SUPERINTENDENT) && (
            <FormGroup label="Approver" htmlFor="superintendentId">
              <ReactSelect
                options={superintendentOptions}
                value={superintendentOptions.find(
                  (option: SelectOption) =>
                    option.value === values.superintendentId,
                )}
                onChange={(
                  option: SingleValue<SelectOption> | MultiValue<SelectOption>,
                ) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setFieldValue('superintendentId', option.value || null);
                  } else {
                    // Handle clearing the selection
                    setFieldValue('superintendentId', null);
                  }
                }}
                placeholder="Select superintendent"
                isClearable
                isDisabled={
                  role === UserRoles.SUPERINTENDENT ||
                  initialData?.status === 'completed'
                }
              />
            </FormGroup>
          )}
      </div>

      {/* Loading states */}
      {(crewsLoading || orgsLoading || supLoading || budgetLoading) && (
        <div className="flex justify-center items-center py-4">
          <div className="text-gray-500">
            {budgetLoading
              ? 'Checking bid status...'
              : 'Loading assignment options...'}
          </div>
        </div>
      )}

      {/* Budget Request Modal */}
      {showBudgetModal && taskId && initialData && (
        <BudgetRequestModal
          open={showBudgetModal}
          onClose={handleBudgetModalClose}
          onSubmit={handleBudgetRequestSubmit}
          task={initialData as Task}
          isSubmitting={budgetActionLoading}
        />
      )}

      {/* Cancel Warning Modal */}
      {showCancelWarning && cancelType && (
        <CancelWarningModal
          open={showCancelWarning}
          onClose={handleCancelWarningClose}
          onConfirm={handleCancelWarningConfirm}
          cancelType={cancelType}
          organizationName={
            cancelType === 'delegation' ? delegatedToName : undefined
          }
        />
      )}
    </>
  );
};
